import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { productsAPI, categoriesAPI, bannersAPI, analyticsAPI, getImageUrl } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import {
  ArrowRight, Package, Shield, Truck, Star, MessageCircle,
  ChevronRight, Zap, Award
} from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

/* ── Category icon map (fallback) ─────────────────────────── */
const CATEGORY_ICONS = {
  default: Package,
};

const HomePage = () => {
  const { data: homeData, isLoading: loading } = useApiQuery(
    ['homeData'],
    async () => {
      const [bannersRes, productsRes, categoriesRes] = await Promise.all([
        bannersAPI.getAll({ is_active: true }),
        productsAPI.getAll({ is_active: true, limit: 8 }),
        categoriesAPI.getAll(),
      ]);
      return {
        banners: bannersRes.data,
        featuredProducts: productsRes.data.data,
        categories: categoriesRes.data.slice(0, 8),
      };
    }
  );

  const banners          = homeData?.banners          || [];
  const featuredProducts = homeData?.featuredProducts || [];
  const categories       = homeData?.categories       || [];

  useEffect(() => {
    analyticsAPI.track({ event_type: 'page_view', page: 'home' }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <AnnouncementBar />
      <Navbar />

      {/* ══ HERO BANNER SLIDER ══════════════════════════════════ */}
      <section className="relative" data-testid="hero-section">
        {loading ? (
          <div className="w-full h-[420px] md:h-[560px] skeleton" />
        ) : banners.length > 0 ? (
          <Swiper
            modules={[Autoplay, Pagination]}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop
            className="w-full h-[420px] md:h-[560px]"
          >
            {banners.map((banner) => (
              <SwiperSlide key={banner.id}>
                <div className="relative w-full h-full">
                  <img
                    src={getImageUrl(banner.image_url)}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/80 via-[#0f172a]/40 to-transparent" />
                  <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-6 md:px-12 max-w-7xl">
                      <div className="max-w-xl slide-up">
                        <h1
                          className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {banner.title}
                        </h1>
                        {banner.redirect_url && (
                          <Link to={banner.redirect_url}>
                            <button className="inline-flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold px-8 py-3.5 rounded-full shadow-lg transition-all hover:shadow-xl hover:gap-3">
                              Shop Now
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          /* Fallback hero when no banners */
          <div
            className="relative w-full h-[420px] md:h-[560px] flex items-center overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a2e 50%, #0f172a 100%)' }}
          >
            {/* Decorative circles */}
            <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-[#8acb95]/10" />
            <div className="absolute -right-20 -bottom-20 w-[400px] h-[400px] rounded-full bg-[#8acb95]/5" />

            <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
              <div className="max-w-xl fade-in">
                <div className="inline-flex items-center gap-2 bg-[#8acb95]/20 text-[#8acb95] text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
                  <Zap className="w-3.5 h-3.5" />
                  1600+ Premium Products
                </div>
                <h1
                  className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Discover<br />
                  <span className="text-[#8acb95]">Gulf Star</span><br />
                  Collection
                </h1>
                <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                  Premium electronics, computers & accessories. Browse and order with ease.
                </p>
                <Link to="/products">
                  <button className="inline-flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold px-8 py-4 rounded-full shadow-lg transition-all hover:gap-3 text-base">
                    Browse Products
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ══ TRUST BAR ════════════════════════════════════════════ */}
      <section className="bg-white border-b border-slate-100 py-6" data-testid="trust-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Quality Assured', desc: '100% Authentic Products' },
              { icon: Truck, title: 'Fast Delivery', desc: 'Quick & Reliable Shipping' },
              { icon: Award, title: '5-Star Rated', desc: 'Trusted by Thousands' },
              { icon: Package, title: '1600+ Products', desc: 'Wide Selection Available' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-3">
                <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#8acb95]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {title}
                  </h3>
                  <p className="text-xs text-slate-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CATEGORIES ═══════════════════════════════════════════ */}
      <section className="py-14" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#8acb95] text-sm font-semibold uppercase tracking-wider mb-1">Browse By</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-slate-800"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Shop by Category
              </h2>
            </div>
            <Link
              to="/products"
              className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-[#8acb95] hover:text-[#6ab57e] transition-colors"
            >
              All Products <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-28 bg-slate-100 rounded-2xl skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
              {categories.map((category) => (
                <Link
                  key={category.name}
                  to={`/products?category=${encodeURIComponent(category.name)}`}
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group bg-white hover:bg-[#8acb95] rounded-2xl p-4 transition-all duration-300 border border-slate-100 hover:border-[#8acb95] hover:shadow-lg text-center"
                >
                  <div className="w-10 h-10 bg-green-50 group-hover:bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors">
                    <Package className="w-5 h-5 text-[#8acb95] group-hover:text-white transition-colors" />
                  </div>
                  <h3
                    className="font-semibold text-xs text-slate-700 group-hover:text-white transition-colors line-clamp-2 leading-snug"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {category.name}
                  </h3>
                  <p className="text-[10px] text-slate-400 group-hover:text-white/70 mt-1 transition-colors">
                    {category.count} items
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ FEATURED PRODUCTS ════════════════════════════════════ */}
      <section className="py-14 bg-white" data-testid="featured-products-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#8acb95] text-sm font-semibold uppercase tracking-wider mb-1">Hand-picked</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-slate-800"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Featured Products
              </h2>
            </div>
            <Link
              to="/products"
              className="flex items-center gap-1.5 text-sm font-semibold text-[#8acb95] hover:text-[#6ab57e] transition-colors"
              data-testid="view-all-products"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl overflow-hidden">
                  <div className="aspect-[4/3] skeleton" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-slate-200 rounded skeleton w-1/3" />
                    <div className="h-4 bg-slate-200 rounded skeleton" />
                    <div className="h-4 bg-slate-200 rounded skeleton w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ PROMO BANNER ═════════════════════════════════════════ */}
      <section className="py-6 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className="rounded-3xl overflow-hidden relative flex items-center min-h-[180px] md:min-h-[220px] px-8 md:px-16"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1a3d2e 60%, #0f172a 100%)' }}
          >
            {/* Decorative */}
            <div className="absolute right-0 top-0 w-80 h-80 rounded-full bg-[#8acb95]/10 -translate-y-1/2 translate-x-1/2" />
            <div className="absolute right-20 bottom-0 w-48 h-48 rounded-full bg-[#8acb95]/5 translate-y-1/2" />

            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-6">
              <div>
                <p className="text-[#8acb95] font-semibold text-sm uppercase tracking-wider mb-2">
                  Gulf Star Trading
                </p>
                <h2
                  className="text-3xl md:text-4xl font-bold text-white leading-tight"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  1600+ Products<br className="hidden md:block" />
                  <span className="text-[#8acb95]"> Wholesale & Retail</span>
                </h2>
              </div>
              <Link to="/products" className="flex-shrink-0">
                <button className="inline-flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold px-8 py-4 rounded-full transition-all hover:shadow-lg hover:gap-3 text-sm">
                  Browse All Products
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHATSAPP CTA ═════════════════════════════════════════ */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <div className="w-16 h-16 bg-[#25D366]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <WhatsAppIcon className="w-14 h-14" />
          </div>
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-800 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Need Help Choosing?
          </h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">
            Chat with our team on WhatsApp for instant product recommendations and bulk order pricing.
          </p>
          <a
            href="https://wa.me/1234567890?text=Hello, I would like to inquire about your products."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#1db954] text-white font-semibold px-8 py-4 rounded-full shadow-lg transition-all hover:shadow-xl text-sm"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Chat on WhatsApp
          </a>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
